import type { NexusValidationResultSummary } from '../../../../domain/nexus/model';

export type { NexusValidationResultSummary };

export const ACTIVE_NEXUS_UPLOAD_STATES = new Set([
  'SUBMITTING',
  'PENDING',
  'IN_PROGRESS',
  'UNKNOWN',
]);

export function isActiveNexusUploadStatus(status: string): boolean {
  return ACTIVE_NEXUS_UPLOAD_STATES.has(status);
}

/**
 * Polling can only advance uploads Nexus accepted. Without a remote project id
 * (e.g. a submission whose acceptance is unknown) the server skips the refresh,
 * so polling would just reload the same data forever.
 */
export function isPollableNexusUpload(
  upload: { status: string; remoteProjectId: string | null } | null | undefined,
): boolean {
  return !!upload?.remoteProjectId && isActiveNexusUploadStatus(upload.status);
}

export function formatNexusBytes(bytes: number | null | undefined): string {
  if (!Number.isFinite(bytes) || !bytes || bytes < 1) return '0 B';
  const units = ['B', 'KiB', 'MiB', 'GiB'];
  const unit = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / 1024 ** unit;
  return `${value >= 10 || unit === 0 ? value.toFixed(0) : value.toFixed(1)} ${units[unit]}`;
}

export function formatNexusTimestamp(value: string | null | undefined): string {
  if (!value) return 'Never';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
}

export function diagnosticMessage(value: unknown): string {
  if (typeof value === 'string' && value.trim()) return value;
  if (!value || typeof value !== 'object') return 'Google Nexus returned an unknown error.';
  const record = value as Record<string, unknown>;
  if (typeof record.message === 'string' && record.message.trim()) {
    // Pre-flight reasons (INVALID_DELIVERY_NAME, HARBOR_SCHEMA_MISMATCH) name the fix.
    const reason = typeof record.reason === 'string' && record.reason.trim() ? record.reason : null;
    return reason && !record.message.includes(reason) ? `${reason}: ${record.message}` : record.message;
  }
  if (record.error) return diagnosticMessage(record.error);
  return 'Google Nexus returned an unknown error.';
}


function asRecord(value: unknown): Record<string, any> | null {
  return value && typeof value === 'object' ? (value as Record<string, any>) : null;
}

export function summarizeNexusValidationResponse(
  value: unknown,
): NexusValidationResultSummary | null {
  const root = asRecord(value);
  if (!root) return null;
  const response = asRecord(root.response) || root;
  const v2 = asRecord(response.validationResultsV2);
  const legacy = asRecord(response.validationResults);
  const v2Results = v2?.results;
  const legacyResults = legacy?.results;
  const results = Array.isArray(v2Results)
    ? v2Results
    : Array.isArray(legacyResults)
      ? legacyResults
      : [];
  const statusMessage =
    typeof root.statusMessage === 'string'
      ? root.statusMessage
      : typeof response.statusMessage === 'string'
        ? response.statusMessage
        : null;
  if (!results.length && !statusMessage) return null;

  const summary: NexusValidationResultSummary = {
    total: results.length,
    passed: 0,
    failed: 0,
    skipped: 0,
    errored: 0,
    statusMessage,
  };
  for (const result of results) {
    const record = asRecord(result);
    const state = String(record?.validationState || record?.state || '')
      .replace(/^VALIDATION_STATE_/, '')
      .toUpperCase();
    if (state === 'PASSED') summary.passed += 1;
    else if (state === 'FAILED') summary.failed += 1;
    else if (state === 'SKIPPED') summary.skipped += 1;
    else if (state === 'ERRORED') summary.errored += 1;
  }
  return summary;
}

export interface NexusUploadPreviewInput {
  validationFormat: 'dockerWorlds' | 'harbor';
  requestName: string;
  deliveryBugId: string;
  testRun: boolean;
  validatorNames: string[];
  worldName: string;
  taskId: string;
  batchName: string;
  containerImageUri: string;
  maxValidationTasks: number | null;
  mcpStartupWaitTimeSeconds: number | null;
  archiveName: string | null;
  archiveSizeBytes: number | null;
}

/** Mirrors the non-secret request summary persisted by the callable. */
export function buildNexusUploadPreview(input: NexusUploadPreviewInput): unknown {
  const common = {
    tarBytes: input.archiveName
      ? `[staged from ${input.archiveName}; ${formatNexusBytes(input.archiveSizeBytes)}]`
      : '[select a .tar archive]',
    containerImageUri: input.containerImageUri || '(required)',
  };
  const validationData =
    input.validationFormat === 'harbor'
      ? {
          harborData: {
            ...common,
            taskId: input.taskId || '(required)',
            batchName: input.batchName || '(required)',
          },
        }
      : {
          dockerWorldsData: {
            ...common,
            worldName: input.worldName || '(required)',
            ...(input.maxValidationTasks !== null
              ? { maxValidationTasks: String(input.maxValidationTasks) }
              : {}),
            ...(input.mcpStartupWaitTimeSeconds !== null
              ? { mcpStartupWaitTimeSeconds: input.mcpStartupWaitTimeSeconds }
              : {}),
          },
        };
  return {
    name: input.requestName || '(select a project)',
    deliveryBugId: input.deliveryBugId || '(required)',
    enableAsync: true,
    testRun: input.testRun,
    ...(input.validatorNames.length ? { validatorNames: input.validatorNames } : {}),
    validationData,
  };
}
